import { client } from '@/graphql/client';
import { profileQuery } from '@/graphql/query';
import { queryClient } from '@/util/reactQuery';
import { useSuspenseQuery } from '@tanstack/react-query';

export type UserRole =
  | 'industry'
  | 'admin'
  | 'customer'
  | 'employee'
  | 'read_only_admin'
  | 'supplier';

export interface User<TPermissions = Set<string>> {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  photo?: string;
  loggedAsAdmin?: boolean;
  chains: [];
  services: [];
  permissions: TPermissions;
  excludedChains: {
    read: string[];
    write: string[];
  };
  company?: string;
  phone: string;
  linkedIndustry?: string;
}

interface ProfileQuery {
  profile: User;
}

export const initialProfile = async () => {
  const { data } = await client.query<ProfileQuery>({
    query: profileQuery,
    fetchPolicy: 'network-only'
  });
  return data.profile;
};

export const useProfile = () => {
  const user = usePossibleUser();

  if (!user) {
    throw new Error('User not initialized');
  }

  return user;
};

export const usePossibleUser = () => {
  const { data } = useSuspenseQuery({
    queryKey: ['profile'],
    queryFn: initialProfile
  });
  if (data) {
    return {
      ...data,
      chains: data.chains || [],
      services: data.services || [],
      permissions: new Set(data.permissions)
    };
  }
  return null;
};

export const useRole = (...roles: UserRole[]) => {
  const user = usePossibleUser();
  return roles.some((role) => user?.role === role);
};

export const useLoggedIn = () => {
  const user = usePossibleUser();
  return Boolean(user);
};

export const revalidateProfile = () => {
  queryClient.invalidateQueries({
    queryKey: ['profile']
  });
};
